import { useCallback } from 'react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { isAskUserFeedbackStagedAction } from '@renderer/chat/integrations/ask-user-feedback'
import type { AnswerableProgress } from '@renderer/chat/integrations/answerable'
import type { AskUserCardProgress } from '@renderer/chat/integrations/ask-user-card'
import { ActionViewFeedbackCard } from '../views/action-view/nested/feedback-card'
import { ActionViewQuestionCard } from '../views/action-view/nested/question-card'
import { useArchiveStagedActions, type StagedAction } from '../hooks/use-staged-actions'

interface PendingAnswerableCardProps {
  action: StagedAction
  /** Amplitude `source` for the dismissal, since three views mount this. */
  source: 'action-view' | 'chat-output' | 'chat-view' | 'chat-drawer'
  /** Combined step numbering when a bar walks several asks in one session. */
  progress?: AskUserCardProgress
  onProgress?: (answer: AnswerableProgress) => void
}

/**
 * A pending AskUserQuestion / AskUserFeedback as a floating bar owns it: the
 * right card for the ask, with the dismissal in its footer.
 *
 * Three bars hoist one of these above their composer: the Action View, the chat
 * output's batch bar, and the Chat View. One component rather than three copies
 * of the dispatch, because a control added to the row has to reach all three.
 * The dismissal is the current one (HIG-6042) — a card owns the whole row while
 * it is up, which puts the control row's Archive off screen, and every exit the
 * card itself offers posts to an endpoint that refuses an action the server has
 * already resolved.
 */
export function PendingAnswerableCard({ action, source, progress, onProgress }: PendingAnswerableCardProps) {
  const { dismissStagedAction } = useArchiveStagedActions()

  // Archiving releases the run waiting on the card (see `releaseAnswerableRun`).
  // No `closeView` afterwards, unlike the control row's Archive: two of the
  // three hosts are not the action's own view, and archiving already moves the
  // Action View to `ready`, so the card retires in place with Restore where it
  // stood.
  const handleDismiss = useCallback(() => {
    dismissStagedAction(action.id).catch(console.error)
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'archive',
      ...stagedActionEventProperties(action),
      source,
    })
  }, [action, dismissStagedAction, source])

  return isAskUserFeedbackStagedAction(action) ? (
    <ActionViewFeedbackCard action={action} progress={progress} onProgress={onProgress} onDismiss={handleDismiss} />
  ) : (
    <ActionViewQuestionCard action={action} progress={progress} onProgress={onProgress} onDismiss={handleDismiss} />
  )
}
